// Ogma Quote's cards on Home (docs/specs/CONNECT.md section 7): every write goes through act.
//   approve_quote          9.17 a priced quote that waits for you (the first 20 of a price version, a referral, a decline
//                          rule, a guardrail, the kill switch or your gate): approve it and it goes to the customer, or
//                          withdraw it with the reason
//   confirm_quote_answers  9.16 the answers Ogma read from the conversation (or that are missing): check, correct and
//                          confirm, and the quote is priced at once
//   quote_outcome          9.19 the customer accepted or declined on the quote page: what they chose, then Done
//   portal_answers         9.27 a customer answered a flow on the quote portal and was told you would be in touch: their
//                          answers (and fit), then Done
//   portal_question        9.28 the quote assistant passed a customer's question to you (nothing approved to answer it
//                          with, or the price and terms): reply to them, answer it into Quotes › Knowledge, then Done
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { fmtDate } from "../../lib/format";
import { answersFrom, DECLINE_LABEL, formatMoney, quotePageUrl, STATUS_LABEL, waitText, type FieldValue } from "../../lib/quotes";
import { Async, Empty, KeyValues, Notice } from "../../components/ui";
import { AnswerFields, answerText, PriceResult } from "../quote/Pricing";
import { Decide, OtherTask } from "./TaskCards";
import "../quote/pricing.css";

type CardProps = { crm: Crm; task: HumanTask; onDone: () => void };

const money = (n: unknown, c: unknown) => (typeof n === "number" ? formatMoney(n, typeof c === "string" ? c : "GBP") : "");

export function approveQuoteSummary(t: HumanTask): string {
  const p = t.payload ?? {};
  const total = money(p.total, p.currency);
  return `${String(p.label ?? "A quote")} for ${String(p.organisation ?? "a firm")}${total ? `: ${total}` : ""}`;
}

export function confirmAnswersSummary(t: HumanTask): string {
  const p = t.payload ?? {};
  return `Check the answers for ${String(p.label ?? "a quote")} (${String(p.organisation ?? "a firm")})`;
}

export function quoteOutcomeSummary(t: HumanTask): string {
  const p = t.payload ?? {};
  const who = String(p.organisation ?? "A firm");
  if (p.outcome === "accepted") return `${who} accepted ${String(p.label ?? "your quote")}${money(p.total, p.currency) ? ` (${money(p.total, p.currency)})` : ""}`;
  return `${who} declined ${String(p.label ?? "your quote")}${p.reason ? `: ${(DECLINE_LABEL[String(p.reason)] ?? String(p.reason)).toLowerCase()}` : ""}`;
}

function FirmLink({ id, name }: { id?: string; name?: string }) {
  return id ? <Link to={`/contacts/companies/${id}`}>{name ?? "the firm"}</Link> : <>{name ?? "the firm"}</>;
}

export function ApproveQuoteTask({ crm, task, onDone }: CardProps) {
  const id = String(task.payload.quote_id ?? "");
  const q = useAsync(() => crm.quote(id), [crm, id]);
  return (
    <Async state={q} empty={(x) => (!x ? <Empty title="This quote no longer exists" /> : null)}>
      {(x) => {
        const quote = x!;
        const why = waitText(quote.check_reasons);
        return (
          <div>
            <p className="card-lede">
              <strong>{quote.label}</strong> for <FirmLink id={quote.organisation_id} name={quote.organisation} />
              {quote.person ? <span className="muted"> · to {quote.person}</span> : null}
            </p>
            {why && <p>It waits for you because {why}.</p>}
            {quote.version_seq && quote.check_reasons.includes("first_20") && (
              <p className="muted small">
                Quote {quote.version_seq} of the first {String(task.payload.check_first ?? 20)} from version {quote.price_version}. After those, quotes inside your guardrails go by themselves.
              </p>
            )}
            {quote.result ? <PriceResult result={quote.result} /> : <p className="muted">Not priced yet.</p>}
            {quote.status === "draft" && quote.priced_at ? (
              <>
                <p className="muted small">Approve and Ogma sends the customer a link to the quote, where they choose an option, accept and sign. Reject and the quote is withdrawn.</p>
                <Decide
                  approveLabel="Approve and send"
                  onApprove={async () => {
                    await crm.decideQuote(quote.id, true);
                    onDone();
                  }}
                  onReject={async (note) => {
                    await crm.decideQuote(quote.id, false, note);
                    onDone();
                  }}
                />
              </>
            ) : (
              <p className="muted">This quote has already been decided ({STATUS_LABEL[quote.status].toLowerCase()}).</p>
            )}
          </div>
        );
      }}
    </Async>
  );
}

export function ConfirmAnswersTask({ crm, task, onDone }: CardProps) {
  const id = String(task.payload.quote_id ?? "");
  const q = useAsync(() => crm.quote(id), [crm, id]);
  const [text, setText] = useState<Record<string, FieldValue> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const issues = Array.isArray(task.payload.issues) ? (task.payload.issues as string[]) : [];
  return (
    <Async state={q} empty={(x) => (!x ? <Empty title="This quote no longer exists" /> : null)}>
      {(x) => {
        const quote = x!;
        const questions = quote.questions ?? [];
        const values = text ?? answerText(quote.answers);
        const confirm = async () => {
          setBusy(true);
          setError(null);
          try {
            await crm.confirmQuoteAnswers(quote.id, answersFrom(questions, values));
            onDone();
          } catch (e) {
            setError((e as Error).message);
            setBusy(false);
          }
        };
        return (
          <div>
            <p className="card-lede">
              <strong>{quote.label}</strong> for <FirmLink id={quote.organisation_id} name={quote.organisation} />
            </p>
            <p>
              {Object.keys(quote.answers ?? {}).length
                ? "Ogma read these answers from the conversation. Check them, fill in anything missing, and confirm: the quote is then priced by your price list."
                : "Ogma couldn't find the answers in the conversation. Fill them in and confirm: the quote is then priced by your price list."}
            </p>
            {issues.length > 0 && (
              <ul className="price-problems plain-list">
                {issues.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            )}
            {quote.status !== "draft" ? (
              <p className="muted">This quote has moved on ({STATUS_LABEL[quote.status].toLowerCase()}).</p>
            ) : (
              <>
                <AnswerFields questions={questions} values={values} onChange={setText} sources={quote.answers_sources} />
                <Notice text={error} tone="bad" />
                <div className="action-bar">
                  <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void confirm()}>
                    {busy ? "Pricing…" : "Confirm and price"}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      }}
    </Async>
  );
}

export function QuoteOutcomeTask({ crm, task, onDone }: CardProps) {
  const id = String(task.payload.quote_id ?? "");
  const q = useAsync(() => crm.quote(id), [crm, id]);
  return (
    <Async state={q} empty={(x) => (!x ? <OtherTask crm={crm} task={task} onDone={onDone} /> : null)}>
      {(x) => {
        const quote = x!;
        const accepted = quote.status === "accepted" || quote.status === "paid";
        return (
          <div>
            <p className="card-lede">
              <FirmLink id={quote.organisation_id} name={quote.organisation} /> {accepted ? "accepted" : "declined"} <strong>{quote.label}</strong>.
            </p>
            <KeyValues
              rows={
                accepted
                  ? [
                      ["Option", quote.result?.options.find((o) => o.key === quote.accepted_option)?.label ?? quote.accepted_option ?? "—"],
                      ["Total", money(quote.accepted_total, quote.currency) || "—"],
                      ["Accepted by", `${quote.accepted_name ?? "—"} on ${fmtDate(quote.accepted_at)}`],
                      ["Signing", quote.signing?.status ? `Documenso: ${quote.signing.status}` : "Not set up: send your agreement yourself, or add a Documenso template for quotes"],
                    ]
                  : [
                      ["Why", DECLINE_LABEL[quote.decline_reason ?? ""] ?? quote.decline_reason ?? "—"],
                      ["Their note", quote.decline_note ?? "—"],
                    ]
              }
            />
            <p className="small">
              <FirmLink id={quote.organisation_id} name="Open the firm and its deal" /> ·{" "}
              <a href={quotePageUrl(quote.page_token)} target="_blank" rel="noreferrer">
                See the quote page
              </a>
            </p>
            <OtherTask crm={crm} task={{ ...task, payload: {} }} onDone={onDone} />
          </div>
        );
      }}
    </Async>
  );
}

export function portalAnswersSummary(t: HumanTask): string {
  const p = t.payload ?? {};
  return `${String(p.organisation ?? "A customer")} answered ${String(p.flow ?? "your questions")} on the portal`;
}

/** 9.27 What a customer answered on the portal, with where each answer came from, then Done. */
export function PortalAnswersTask({ crm, task, onDone }: CardProps) {
  const id = String(task.payload.response_id ?? "");
  const r = useAsync(() => crm.quoteResponse(id), [crm, id]);
  return (
    <Async state={r} empty={(x) => (!x ? <OtherTask crm={crm} task={task} onDone={onDone} /> : null)}>
      {(x) => {
        const resp = x!;
        const asked = resp.definition.pages.flatMap((p) => p.sections.flatMap((s) => s.questions)).filter((q) => q.type !== "info" && q.key in resp.answers);
        return (
          <div>
            <p className="card-lede">
              <FirmLink id={String(task.payload.organisation_id ?? "")} name={String(task.payload.organisation ?? "They")} /> answered <strong>{resp.flow}</strong> and were told you would be in touch.
            </p>
            {resp.result && (
              <p>
                <span className={`chip ${resp.result.fit ? "chip-ok" : "chip-warn"}`}>{resp.result.fit ? "A fit" : "Not a fit"}</span> {resp.result.reason ?? ""}
              </p>
            )}
            <KeyValues rows={asked.map((q) => [q.label, answerWords(resp.answers[q.key])])} />
            <OtherTask crm={crm} task={{ ...task, payload: {} }} onDone={onDone} />
          </div>
        );
      }}
    </Async>
  );
}

export function portalQuestionSummary(t: HumanTask): string {
  const p = t.payload ?? {};
  return `${String(p.organisation ?? "A customer")} asked: “${String(p.question ?? "a question")}”`;
}

/** 9.28 A question the assistant passed on: what they asked and why it came to you; answer it, then Done. */
export function PortalQuestionTask({ crm, task, onDone }: CardProps) {
  const p = task.payload ?? {};
  return (
    <div>
      <p className="card-lede">
        <FirmLink id={String(p.organisation_id ?? "")} name={String(p.organisation ?? "A customer")} /> asked the assistant on <strong>{String(p.flow ?? "your questions")}</strong>:
      </p>
      <blockquote className="question-quoted">{String(p.question ?? "")}</blockquote>
      <p className="small">{String(p.why ?? "")} They were told you would get back to them.</p>
      <p className="small">
        Reply to them, then <Link to="/quotes/knowledge">answer it in Quotes › Knowledge</Link> if others will ask the same. Any later questions are on the firm's page.
      </p>
      <OtherTask crm={crm} task={{ ...task, payload: {} }} onDone={onDone} />
    </div>
  );
}

/** An answer as the card shows it: yes or no, a list, a file's name, or a group's items. */
function answerWords(v: unknown): string {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.map((x) => (x && typeof x === "object" ? Object.values(x).join(", ") : String(x))).join("; ") || "None";
  if (v && typeof v === "object" && "name" in v) return String((v as { name: unknown }).name);
  return v === undefined || v === null ? "—" : String(v);
}
